import { useState, useRef, useEffect } from 'react'
import { MessageCircle, ChevronDown, Send, X, ArrowDownCircle } from 'react-feather'
import liscrLogo from '../../assets/images/liscr_logo.png'
import favicon from '../../assets/images/liscerfavicon.png'
import GlobalVariable from '../../settings/global'
import axios from 'axios'

const ChatbotWidget = () => {
  const [open, setOpen] = useState(false)
  const [input, setInput] = useState('')
  const [isTyping, setIsTyping] = useState(false)
  const [showScrollButton, setShowScrollButton] = useState(false)
  const [messages, setMessages] = useState([
    {
      type: 'bot',
      text: 'Welcome!<br/>Thank you for connecting with us. We are here to assist you. Please share your questions or requests.',
      isHtml: true
    }
  ])

  const messagesEndRef = useRef(null)
  const chatBodyRef = useRef(null)
  const animationIndexRef = useRef(-1)
  const animatedWordsRef = useRef([])
  const isAnimatingRef = useRef(false)
  const shouldAutoScrollRef = useRef(true)

  useEffect(() => {
    if (!open) return
    const chatBody = chatBodyRef.current
    if (!chatBody) return
    const handleScroll = () => {
      const isAtBottom =
        chatBody.scrollTop + chatBody.clientHeight >= chatBody.scrollHeight - 10
      setShowScrollButton(!isAtBottom)
      shouldAutoScrollRef.current = isAtBottom
    }
    chatBody.addEventListener('scroll', handleScroll, { passive: true })
    return () => chatBody.removeEventListener('scroll', handleScroll)
  }, [open])


  const handleSend = async () => {
    const trimmed = input.trim()
    if (!trimmed) return

    setMessages(prev => [...prev, { type: 'user', text: trimmed }])
    setInput('')
    setIsTyping(true)

    try {
      const response = await axios.post(
        `${GlobalVariable.hostpath}api/PUB/RLM/SendChatRequest`,
        { question: trimmed }
      )

      const botResponse =
        response?.data?.botResponse ||
        '✅ Received your message, but no reply was found.'

      setIsTyping(false)

      const words = botResponse.split(' ')
      animatedWordsRef.current = []
      animationIndexRef.current = messages.length + 1
      isAnimatingRef.current = true

      setMessages(prev => [
        ...prev,
        {
          type: 'bot',
          text: '',
          isHtml: true
        }
      ])

      let i = 0
      const interval = setInterval(() => {
        if (i < words.length) {
          animatedWordsRef.current.push(words[i])
          setMessages(prev => {
            const updated = [...prev]
            updated[animationIndexRef.current] = {
              ...updated[animationIndexRef.current],
              text: animatedWordsRef.current.join(' ')
            }
            return updated
          })
          i++
        } else {
          clearInterval(interval)
          isAnimatingRef.current = false
        }
      }, 80)
    } catch (error) {
      console.error('API error:', error)
      setMessages(prev => [
        ...prev,
        {
          type: 'bot',
          text: '⚠️ Unable to reach server. Please try again later.',
          isHtml: false
        }
      ])
      setIsTyping(false)
      isAnimatingRef.current = false
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSend()
  }

  useEffect(() => {
    if (isAnimatingRef.current && shouldAutoScrollRef.current && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'auto' })
    }

    const chatBody = chatBodyRef.current
    if (!chatBody) return

    const isAtBottom =
      chatBody.scrollTop + chatBody.clientHeight >= chatBody.scrollHeight - 20
    setShowScrollButton(!isAtBottom)
    shouldAutoScrollRef.current = isAtBottom
  }, [messages])

  return (
    <div
      className="chatbot-widget"
      style={{ display: 'none' }}
    >
      <button
        className="chatbot-toggle"
        onClick={() => setOpen(!open)}
        aria-label="Toggle Chatbot"
      >
        {open ? <X size={22} /> : <MessageCircle size={22} />}
      </button>

      {open && (
        <div className="chatbot-panel">
          <div className="chatbot-header">
            <img src={liscrLogo} alt="Liberian Registry" className="chatbot-logo" />
            <ChevronDown onClick={() => setOpen(false)} className="chatbot-close-icon chatbotheaderClear" />
          </div>

          <div className="chatbot-body" ref={chatBodyRef}>
            {messages.map((msg, index) => (
              <div key={index} className={`chat-message ${msg.type}`}>
                {msg.type === 'bot' && (
                  <img src={favicon} alt="bot" className="chat-avatar" />
                )}
                <div className={`chat-bubble ${msg.type}`}>
                  {msg.isHtml ? (
                    <div dangerouslySetInnerHTML={{ __html: msg.text }} />
                  ) : (
                    msg.text
                  )}
                </div>
              </div>
            ))}

            {isTyping && (
              <div className="chat-message bot">
                <img src={favicon} alt="bot" className="chat-avatar" />
                <div className="chat-bubble bot typing-indicator">
                  <span className="dot" />
                  <span className="dot" />
                  <span className="dot" />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
          {(showScrollButton) && (
            <button
              className="scroll-to-bottom-btn"
              onClick={() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })}
              aria-label="Scroll to bottom"
            >
              <ArrowDownCircle size={20} />
            </button>
          )}

          <div className="chatbot-input">
            <input
              type="text"
              placeholder="Message..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
            />
            <button onClick={handleSend}>
              <Send size={18} />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default ChatbotWidget
